import React, {useState, useEffect, useCallback, useRef} from "react";
import PropTypes from 'prop-types';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Form,
  FormGroup,
  Label,
  Input,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Row,
  Col,
  Alert,
  Spinner
} from "reactstrap";
import { Link } from "react-router-dom";
import classnames from 'classnames';
import Cropper from 'react-easy-crop';
import imageCompression from 'browser-image-compression';

// i18n
import { withTranslation } from "react-i18next";
// Redux
import { connect, useDispatch, useSelector } from "react-redux";
import withRouter from "../withRouter";
import {
  getUserLinks,
  getUserModules,
  getUserProfile,
  giveUserFeedback,
  uploadProfilePicture,
  updateUserPassword
} from "../../../store/actions/user";
import QuickLinks from "../../../Pages/QuickLinks";
import QuickRoutes from "../../../Pages/QuickRoutes";
import { User, Mail, Briefcase, Calendar, Shield, CheckCircle, Pencil, MessageSquare, Lock, LogOut, Link as LinkIcon, ExternalLink, Layout, RotateCw, ZoomIn, ZoomOut, Move, Crop, Download, Upload } from 'lucide-react';
import GlobalQuickRoutes from '../../../Pages/System-admin/GlobalQuickRoutes/index';
import { getNavigation } from "../../../store/actions/navigation";
import {showSuccessDialog} from "../../../utils/dialogs";

// Custom CSS for range sliders
const rangeSliderStyles = `
  .image-editor-slider {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: #ddd;
    outline: none;
    transition: background 0.2s;
  }
  
  .image-editor-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #007bff;
    cursor: pointer;
  }
  
  .image-editor-slider::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #007bff;
    cursor: pointer;
    border: none;
  }
  
  .image-editor-slider:hover {
    background: #ccc;
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined' && !document.getElementById('image-editor-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'image-editor-styles';
  styleElement.textContent = rangeSliderStyles;
  document.head.appendChild(styleElement);
}

// Utility functions for image processing
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getRadianAngle = (degreeValue) => {
  return (degreeValue * Math.PI) / 180;
};

const rotateSize = (width, height, rotation) => {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0, flip = { horizontal: false, vertical: false }) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d');

  if (!croppedCtx) {
    return null;
  }

  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    croppedCanvas.toBlob((file) => {
      resolve(file);
    }, 'image/jpeg', 0.9);
  });
};

const compressImage = async (file, maxSizeKB = 1024) => {
  const options = {
    maxSizeMB: maxSizeKB / 1024,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.8
  };

  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error('Error compressing image:', error);
    return file;
  }
};

// Custom styles
const styles = {
  dropdown: {
    boxShadow: '0 10px 30px 0 rgba(0,0,0,0.1)',
    border: 'none',
    borderRadius: '12px',
    padding: '10px 0',
    minWidth: '240px',
    marginTop: '10px'
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 20px',
    color: '#495057',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    fontWeight: '500'
  },
  dropdownDivider: {
    margin: '6px 16px',
    opacity: 0.08
  },
  profileButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px',
    background: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    borderRadius: '40px',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)'
  },
  profileImage: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid rgba(255, 255, 255, 0.9)'
  },
  profileIcon: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 123, 255, 0.08)',
    color: '#007bff',
    border: '2px solid rgba(0, 123, 255, 0.2)'
  },
  profileName: {
    marginLeft: '10px',
    marginRight: '8px',
    fontSize: '12px',
    fontWeight: '400',
    color: '#495057'
  },
  headerProfileName: {
    marginLeft: '10px',
    marginRight: '8px',
    fontSize: '13px',
    fontWeight: '400',
    color: '#6c757d'
  },
  modalHeader: {
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    padding: '20px 24px'
  },
  modalBody: {
    padding: '24px'
  },
  modalFooter: {
    borderTop: '1px solid rgba(0,0,0,0.05)',
    padding: '16px 24px',
    justifyContent: 'flex-end'
  },
  navTabs: {
    border: 'none',
    marginBottom: '24px',
    display: 'flex',
    gap: '12px'
  },
  navLink: {
    padding: '10px 16px',
    borderRadius: '8px',
    fontWeight: '500',
    color: '#6c757d',
    transition: 'all 0.2s ease'
  },
  navLinkActive: {
    background: 'rgba(0, 123, 255, 0.08)',
    color: '#007bff',
    fontWeight: '600'
  },
  card: {
    border: 'none',
    borderRadius: '16px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
    background: 'white'
  },
  formInput: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(0,0,0,0.08)',
    fontSize: '14px',
    backgroundColor: '#f8f9fa'
  },
  button: {
    borderRadius: '8px',
    padding: '10px 20px',
    fontWeight: '500',
    boxShadow: 'none',
    fontSize: '14px'
  },
  buttonPrimary: {
    background: '#007bff',
    border: 'none'
  },
  buttonOutline: {
    background: 'transparent',
    border: '1px solid rgba(0,0,0,0.1)',
    color: '#495057'
  },
  profileContainer: {
    borderRadius: '16px',
    background: 'white',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden'
  },
  profileBanner: {
    height: '140px',
    background: 'linear-gradient(to right, #3b82f6, #60a5fa)',
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center'
  },
  profileAvatarWrapper: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    margin: '-60px auto 0',
    position: 'relative',
    zIndex: 5,
    padding: '4px',
    background: 'white',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
  },
  profileAvatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
    backgroundColor: '#f8f9fa'
  },
  profileEditButton: {
    position: 'absolute',
    bottom: '5px',
    right: '5px',
    background: 'white',
    color: '#007bff',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    zIndex: 10
  },
  profileContent: {
    padding: '20px 24px 30px'
  },
  profileHeader: {
    textAlign: 'center',
    marginBottom: '24px'
  },
  profileRole: {
    fontSize: '14px',
    color: '#6c757d',
    marginBottom: '10px'
  },
  badgeContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '8px'
  },
  badgeVerified: {
    display: 'inline-flex',
    alignItems: 'center', 
    padding: '6px 12px',
    borderRadius: '20px',
    backgroundColor: 'rgba(25, 135, 84, 0.1)',
    color: '#198754',
    fontWeight: '500',
    fontSize: '13px'
  },
  badgeNotVerified: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    borderRadius: '20px',
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    color: '#dc3545',
    fontWeight: '500',
    fontSize: '13px'
  },
  profileJoinDate: {
    fontSize: '13px',
    color: '#6c757d',
    textAlign: 'center',
    marginBottom: '20px'
  },
  profileInfoSection: {
    background: '#f8f9fa',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px'
  },
  profileInfoTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#212529',
    marginBottom: '16px'
  },
  profileInfoRow: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '0 -8px'
  },
  profileInfoCol: {
    flex: '0 0 50%',
    padding: '0 8px',
    marginBottom: '16px'
  },
  profileInfoItem: {
    background: 'white',
    borderRadius: '10px',
    padding: '16px',
    height: '100%',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)'
  },
  profileInfoLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#6c757d',
    marginBottom: '8px'
  },
  profileInfoValue: {
    fontSize: '15px',
    color: '#212529',
    marginLeft: '28px'
  },
  profileInfoIcon: {
    marginRight: '8px',
    color: '#007bff'
  },
  // Image Editor Styles
  imageEditorModal: {
    maxWidth: '90vw',
    width: '800px'
  },
  cropContainer: {
    position: 'relative',
    width: '100%',
    height: '400px',
    background: '#333',
    borderRadius: '12px',
    overflow: 'hidden'
  },
  controlsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '20px'
  },
  controlsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px'
  },
  sliderContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: '200px'
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '13px',
    fontWeight: '500'
  },
  actionButtonActive: {
    background: '#007bff',
    color: 'white',
    borderColor: '#007bff'
  },
  previewContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20px',
    padding: '20px',
    background: '#f8f9fa',
    borderRadius: '12px'
  },
  previewImage: {
    maxWidth: '200px',
    maxHeight: '200px',
    borderRadius: '50%',
    border: '3px solid #007bff',
    objectFit: 'cover'
  },
  fileInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    background: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '13px'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  }
};

const ImageEditor = ({ isOpen, toggle, onImageSave, initialImage }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [originalFile, setOriginalFile] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const editorFileInputRef = useRef(null);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setOriginalFile(file);
      setFileInfo({
        name: file.name,
        size: (file.size / 1024).toFixed(2) + ' KB',
        type: file.type
      });
      
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
        setCroppedImage(null);
      });
      reader.readAsDataURL(file);
    }
  };

  const showCroppedImage = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    
    setLoading(true);
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      setCroppedImage(croppedImage);
    } catch (e) {
      console.error('Error cropping image:', e);
    } finally {
      setLoading(false);
    }
  }, [imageSrc, croppedAreaPixels, rotation]);

  const handleSave = async () => {
    if (!croppedImage) {
      await showCroppedImage();
      return;
    }

    setLoading(true);
    try {
      // Compress the image to ensure it's under 1MB
      const compressedImage = await compressImage(croppedImage, 1024);
      
      // Create a new file object with proper name and type
      const finalFile = new File([compressedImage], 
        `profile_${Date.now()}.jpg`, 
        { type: 'image/jpeg' }
      );

      onImageSave(finalFile);
      handleReset();
      toggle();
    } catch (error) {
      console.error('Error saving image:', error);
      alert('Error processing image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setRotation(0);
    setZoom(1);
    setCroppedAreaPixels(null);
    setCroppedImage(null);
    setOriginalFile(null);
    setFileInfo(null);
  };

  const handleChangeImageClick = () => {
    editorFileInputRef.current.click();
  };

  useEffect(() => {
    if (isOpen) {
      setImageSrc(initialImage);
    } else {
      handleReset();
    }
  }, [isOpen, initialImage]);

  const handleCancel = () => {
    handleReset();
    toggle();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" style={{ maxWidth: '90vw' }}>
      <input
        type="file"
        accept="image/png, image/jpeg, image/gif, image/webp"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        ref={editorFileInputRef}
      />
      <ModalHeader toggle={toggle} style={styles.modalHeader}>
        <div className="d-flex align-items-center">
          <Crop size={18} className="me-2 text-primary" />
          <span style={{ fontWeight: '600' }}>Edit Profile Picture</span>
        </div>
      </ModalHeader>
      
      <ModalBody style={styles.modalBody}>
        {loading && (
          <div style={styles.loadingOverlay}>
            <div className="d-flex flex-column align-items-center">
              <Spinner color="primary" />
              <span className="mt-2">Processing image...</span>
            </div>
          </div>
        )}

        {!imageSrc ? (
          <div className="text-center py-5">
            <Upload size={48} className="text-muted mb-3" />
            <h5 className="mb-3">Select an image to edit</h5>
            <p className="text-muted mb-4">
              Supported formats: JPG, PNG, GIF, WEBP, BMP<br />
              Maximum file size: 10MB (will be compressed to under 1MB)
            </p>
            <Button color="primary" onClick={handleChangeImageClick} tag="span">
                <Upload size={16} className="me-2" />
                Choose Image
            </Button>
          </div>
        ) : (
          <>
            {fileInfo && (
              <div style={styles.fileInfo}>
                <span><strong>{fileInfo.name}</strong> ({fileInfo.type})</span>
                <span>{fileInfo.size}</span>
              </div>
            )}

            <div style={styles.cropContainer}>
              <Cropper
                image={imageSrc}
                crop={crop}
                rotation={rotation}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="round"
                showGrid={false}
              />
            </div>

            <div style={styles.controlsContainer}>
              <div style={styles.controlsRow}>
                <div style={styles.sliderContainer}>
                  <ZoomOut size={16} className="text-muted" />
                  <input
                    type="range"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="image-editor-slider"
                  />
                  <ZoomIn size={16} className="text-muted" />
                </div>
                
                <div style={styles.sliderContainer}>
                  <RotateCw size={16} className="text-muted" />
                  <input
                    type="range"
                    value={rotation}
                    min={0}
                    max={360}
                    step={1}
                    onChange={(e) => setRotation(parseInt(e.target.value))}
                    className="image-editor-slider"
                  />
                  <span className="text-muted" style={{ fontSize: '12px', minWidth: '35px' }}>
                    {rotation}°
                  </span>
                </div>
              </div>

              <div style={styles.controlsRow}>
                <div style={styles.actionButtons}>
                  <Button 
                    color="outline-primary" 
                    size="sm" 
                    onClick={showCroppedImage}
                    disabled={!imageSrc}
                  >
                    <Crop size={14} className="me-1" />
                    Preview
                  </Button>
                  <Button 
                    color="outline-secondary" 
                    size="sm" 
                    onClick={() => {
                      setRotation(0);
                      setZoom(1);
                      setCrop({ x: 0, y: 0 });
                    }}
                  >
                    <RotateCw size={14} className="me-1" />
                    Reset
                  </Button>
                  <Button color="outline-info" size="sm" onClick={handleChangeImageClick}>
                      <Upload size={14} className="me-1" />
                      Change Image
                  </Button>
                </div>
              </div>

              {croppedImage && (
                <div style={styles.previewContainer}>
                  <div className="text-center">
                    <h6 className="mb-3">Preview</h6>
                    <img
                      src={URL.createObjectURL(croppedImage)}
                      alt="Cropped"
                      style={styles.previewImage}
                    />
                    <div className="mt-2 text-muted" style={{ fontSize: '12px' }}>
                      Final size: {formatFileSize(croppedImage.size)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </ModalBody>
      
      <ModalFooter style={styles.modalFooter}>
        <Button style={{...styles.button, ...styles.buttonOutline}} onClick={handleCancel}>
          Cancel
        </Button>
        <Button 
          style={{...styles.button, ...styles.buttonPrimary}} 
          onClick={handleSave}
          disabled={!imageSrc || loading}
          className="ms-2"
        >
          {loading ? 'Processing...' : 'Save Profile Picture'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const ChangePasswordModal = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const resetForm = () => {
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  const handleCancel = () => {
    resetForm();
    toggle();
  };

  const validatePasswords = () => {
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (validatePasswords()) {
      try {
        const object={
          password: newPassword,
        }
        const passwordResult=await dispatch(updateUserPassword(object));

        if(passwordResult.success){
          showSuccessDialog("Password Updated Successfully", "success");
        }

        resetForm();
        toggle();
      } catch (err) {
        setError('Failed to change password. Please try again.');
      }
    }
  };

  return (
      <Modal isOpen={isOpen} toggle={toggle} centered>
        <ModalHeader toggle={toggle} style={styles.modalHeader}>
          <div className="d-flex align-items-center">
            <Lock size={18} className="me-2 text-primary" />
            <span style={{ fontWeight: '600' }}>Change Password</span>
          </div>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <ModalBody style={styles.modalBody}>
            {error && (
                <Alert color="danger" className="mb-3 rounded-3">
                  {error}
                </Alert>
            )}

            <FormGroup>
              <Label for="newPassword" className="text-dark fw-medium">
                New Password
              </Label>
              <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={8}
                  style={styles.formInput}
                  className="form-control-lg"
              />
            </FormGroup>

            <FormGroup>
              <Label for="confirmPassword" className="text-dark fw-medium">
                Confirm Password
              </Label>
              <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                  style={styles.formInput}
                  className="form-control-lg"
              />
            </FormGroup>
          </ModalBody>

          <ModalFooter style={styles.modalFooter}>
            <Button style={{...styles.button, ...styles.buttonOutline}} onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              style={{...styles.button, ...styles.buttonPrimary}} 
              type="submit"
              className="ms-2"
            >
              Change Password
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
  );
};

const ProfileModalContent = ({ userProfile }) => {
  if(userProfile === null || userProfile === undefined) {
    window.location.reload();
  }

  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const [imageForEditing, setImageForEditing] = useState(null);
  const [isImageEditorOpen, setIsImageEditorOpen] = useState(false);
  const fileInputRef = useRef(null);
  
  const toggleImageEditor = () => setIsImageEditorOpen(!isImageEditorOpen);

  const handlePencilClick = () => {
    fileInputRef.current.click();
  };
  
  const handleFileChangeForEditor = e => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageForEditing(reader.result);
        toggleImageEditor();
      });
      reader.readAsDataURL(file);
    }
    e.target.value = ''; // To allow selecting the same file again
  };

  const handleImageSave = async (processedFile) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('profileImage', processedFile);
      
      const result = await dispatch(uploadProfilePicture(formData));
      if(result.success) {
        dispatch(getUserProfile());
        showSuccessDialog("Profile picture updated successfully!", "Success");
      }
    } catch (error) {
      console.error('Upload error:', error);
      showSuccessDialog("Failed to update profile picture. Please try again.", "Error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={styles.profileContainer}>
        <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChangeForEditor}
            style={{ display: 'none' }}
            accept="image/png, image/jpeg, image/gif, image/webp"
        />
      <div style={styles.profileBanner}></div>
      
      <div style={styles.profileContent}>
        <div style={styles.profileAvatarWrapper}>
          {userProfile.fullPath ? (
            <img
              src={userProfile.fullPath}
              alt="Profile"
              style={styles.profileAvatarImage}
            />
          ) : (
            <div style={styles.profileAvatarImage} className="d-flex align-items-center justify-content-center">
              <User className="text-primary" size={50} />
            </div>
          )}
          <button 
            onClick={handlePencilClick}
            style={styles.profileEditButton}
            disabled={uploading}
          >
            {uploading ? <Spinner size="sm" /> : <Pencil size={16} />}
          </button>
        </div>

        <div style={styles.profileHeader}>
          <h3 style={{...styles.profileName, fontSize: '20px', fontWeight: '600', color: '#212529', marginBottom: '4px'}}>{userProfile.name}</h3>
          <p style={styles.profileRole}>{userProfile.userType} • {userProfile.organization}</p>
          
          <div style={styles.badgeContainer}>
            {userProfile.isVerified ? 
              <div style={styles.badgeVerified}>
                <CheckCircle size={14} className="me-1" /> Verified
              </div> : 
              <div style={styles.badgeNotVerified}>
                <Shield size={14} className="me-1" /> Not Verified
              </div>
            }
          </div>
          
          <p style={styles.profileJoinDate}>
            Joined {new Date(userProfile.createdAt).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>

        <div style={styles.profileInfoSection}>
          <h4 style={styles.profileInfoTitle}>Profile Information</h4>
          
          <div style={styles.profileInfoRow}>
            <div style={styles.profileInfoCol}>
              <div style={styles.profileInfoItem}>
                <div style={styles.profileInfoLabel}>
                  <User size={16} style={styles.profileInfoIcon} />
                  Full Name
                </div>
                <div style={styles.profileInfoValue}>{userProfile.name}</div>
              </div>
            </div>
            
            <div style={styles.profileInfoCol}>
              <div style={styles.profileInfoItem}>
                <div style={styles.profileInfoLabel}>
                  <Mail size={16} style={styles.profileInfoIcon} />
                  Email
                </div>
                <div style={styles.profileInfoValue}>{userProfile.email}</div>
              </div>
            </div>
            
            <div style={styles.profileInfoCol}>
              <div style={styles.profileInfoItem}>
                <div style={styles.profileInfoLabel}>
                  <Briefcase size={16} style={styles.profileInfoIcon} />
                  Organization
                </div>
                <div style={styles.profileInfoValue}>{userProfile.organization}</div>
              </div>
            </div>
            
            <div style={styles.profileInfoCol}>
              <div style={styles.profileInfoItem}>
                <div style={styles.profileInfoLabel}>
                  <Shield size={16} style={styles.profileInfoIcon} />
                  User Type
                </div>
                <div style={styles.profileInfoValue}>{userProfile.userType}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ImageEditor
        isOpen={isImageEditorOpen}
        toggle={toggleImageEditor}
        onImageSave={handleImageSave}
        initialImage={imageForEditing}
      />
    </div>
  );
};

const FeedbackModal = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const feedbackResponse = await dispatch(giveUserFeedback({title, description}));

    if(feedbackResponse.success) {
      showSuccessDialog("Successfully sent feedback","Success");
      setTitle('');
      setDescription('');
      toggle();
    }
  };

  return (
      <Modal isOpen={isOpen} toggle={toggle} centered>
        <ModalHeader toggle={toggle} style={styles.modalHeader}>
          <div className="d-flex align-items-center">
            <MessageSquare size={18} className="me-2 text-primary" />
            <span style={{ fontWeight: '600' }}>Provide Feedback</span>
          </div>
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody style={styles.modalBody}>
            <FormGroup>
              <Label for="feedbackTitle" className="text-dark fw-medium">
                Title
              </Label>
              <Input
                  id="feedbackTitle"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter a title for your feedback..."
                  required
                  style={styles.formInput}
                  className="form-control-lg"
              />
            </FormGroup>
            <FormGroup>
              <Label for="feedbackDescription" className="text-dark fw-medium">
                Description
              </Label>
              <Input
                  id="feedbackDescription"
                  type="textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please share your feedback here..."
                  rows="5"
                  required
                  style={styles.formInput}
                  className="form-control-lg"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter style={styles.modalFooter}>
            <Button style={{...styles.button, ...styles.buttonOutline}} onClick={toggle}>
              Cancel
            </Button>
            <Button 
              style={{...styles.button, ...styles.buttonPrimary}} 
              type="submit"
              className="ms-2"
            >
              Submit Feedback
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
  );
};

const ProfileMenu = props => {
  const dispatch = useDispatch();
  const userLinks = useSelector((state) => state.users.userLinks)
  const userModules = useSelector((state) => state.users.userModules)
  const userProfile = useSelector((state) => state.users.userProfile)
  const [profileName, setProfileName] = useState("User")

  const navigation = useSelector((state) => state.navigation.list);

  const [menu, setMenu] = useState(false);
  const [username, setUsername] = useState("Admin");
  const [quickLinksModal, setQuickLinksModal] = useState(false);
  const [profileModal, setProfileModal] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [hasQuickModule, setQuickModule] = useState(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleFeedbackModal = () => {
    setShowFeedbackModal(!showFeedbackModal);
  };

  useEffect(() => {
    if (localStorage.getItem("authUser")) {
      if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
        const obj = JSON.parse(localStorage.getItem("authUser"));
        setUsername(obj.displayName);
      } else if (
          process.env.REACT_APP_DEFAULTAUTH === "fake" ||
          process.env.REACT_APP_DEFAULTAUTH === "jwt"
      ) {
        const obj = JSON.parse(localStorage.getItem("authUser"));
        setUsername(obj.username);
      }
    }
  }, [props.success]);

  const toggleQuickLinksModal = () => setQuickLinksModal(!quickLinksModal);
  const toggleProfileModal = () => setProfileModal(!profileModal);
  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  useEffect(() => {
    dispatch(getUserLinks())
    dispatch(getUserModules())
    dispatch(getUserProfile())
    dispatch(getNavigation())
  }, [dispatch]);

  useEffect(() => {
    if (userProfile != null) {
      setProfileName(userProfile.name)
    }
  }, [userProfile]);

  const hasPermission = (data) => data.some(item => item.module_route === "quick/module");

  return (
      <React.Fragment>
        <Dropdown
            isOpen={menu}
            toggle={() => setMenu(!menu)}
            className="d-inline-block"
        >
          <DropdownToggle
              style={styles.profileButton}
              id="page-header-user-dropdown"
              tag="button"
              className="btn shadow-none"
          >
            {userProfile && userProfile.fullPath ? (
              <img 
                src={userProfile.fullPath}
                alt="Profile"
                style={styles.profileImage}
              />
            ) : (
              <div style={styles.profileIcon}>
                <User size={20} />
              </div>
            )}
            <span style={styles.headerProfileName}>{profileName ?? "Refresh once"}</span>
            <i className="mdi mdi-chevron-down d-none d-xl-inline-block" />
          </DropdownToggle>
          
          <DropdownMenu end style={styles.dropdown}>
            <DropdownItem onClick={toggleProfileModal} style={styles.dropdownItem} className="fw-medium">
              <User size={16} className="me-2 text-primary" />
              <span>{props.t("Profile")}</span>
            </DropdownItem>
            
            <DropdownItem onClick={toggleModal} style={styles.dropdownItem} className="fw-medium">
              <Lock size={16} className="me-2 text-primary" />
              <span>{props.t("Change Password")}</span>
            </DropdownItem>
            
            <div className="dropdown-divider" style={styles.dropdownDivider} />
            
            <DropdownItem onClick={toggleQuickLinksModal} style={styles.dropdownItem} className="fw-medium">
              <LinkIcon size={16} className="me-2 text-primary" />
              <span>Quick Links</span>
            </DropdownItem>
            
            <div className="dropdown-divider" style={styles.dropdownDivider} />
            
            <DropdownItem onClick={toggleFeedbackModal} style={styles.dropdownItem} className="fw-medium">
              <MessageSquare size={16} className="me-2 text-primary" />
              <span>{props.t("Feedback")}</span>
            </DropdownItem>
            
            <div className="dropdown-divider" style={styles.dropdownDivider} />
            
            <Link to="/logout" className="dropdown-item fw-medium" style={styles.dropdownItem}>
              <LogOut size={16} className="me-2 text-danger" />
              <span style={{ color: '#dc3545' }}>{props.t("Logout")}</span>
            </Link>
          </DropdownMenu>
        </Dropdown>

        <Modal isOpen={quickLinksModal} toggle={toggleQuickLinksModal} size="xl">
          <ModalHeader toggle={toggleQuickLinksModal} style={styles.modalHeader}>
            <div className="d-flex align-items-center">
              <LinkIcon size={18} className="me-2 text-primary" />
              <span style={{ fontWeight: '600' }}>Quick Access</span>
            </div>
          </ModalHeader>
          <ModalBody style={styles.modalBody}>
            <Nav tabs style={styles.navTabs}>
              <NavItem>
                <NavLink
                    style={{
                      ...styles.navLink,
                      ...(activeTab === '1' ? styles.navLinkActive : {})
                    }}
                    onClick={() => { toggleTab('1'); }}
                >
                  <Layout size={16} className="me-2" />
                  My Forms
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                    style={{
                      ...styles.navLink,
                      ...(activeTab === '2' ? styles.navLinkActive : {})
                    }}
                    onClick={() => { toggleTab('2'); }}
                >
                  <ExternalLink size={16} className="me-2" />
                  External Links
                </NavLink>
              </NavItem>
              {hasPermission(navigation ?? []) && (
                  <NavItem>
                    <NavLink
                        style={{
                          ...styles.navLink,
                          ...(activeTab === '3' ? styles.navLinkActive : {})
                        }}
                        onClick={() => { toggleTab('3'); }}
                    >
                      <LinkIcon size={16} className="me-2" />
                      Global Commonly Used Forms
                    </NavLink>
                  </NavItem>
              )}
            </Nav>
            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                <QuickRoutes />
              </TabPane>
              <TabPane tabId="2">
                <QuickLinks />
              </TabPane>
              <TabPane tabId="3">
                <GlobalQuickRoutes />
              </TabPane>
            </TabContent>
          </ModalBody>
        </Modal>

        <Modal isOpen={profileModal} toggle={toggleProfileModal} size="lg">
          <ModalHeader toggle={toggleProfileModal} style={styles.modalHeader}>
            <div className="d-flex align-items-center">
              <User size={18} className="me-2 text-primary" />
              <span style={{ fontWeight: '600' }}>Profile</span>
            </div>
          </ModalHeader>
          <ModalBody style={styles.modalBody} className="p-0">
            <ProfileModalContent userProfile={userProfile} />
          </ModalBody>
        </Modal>

        <ChangePasswordModal isOpen={isModalOpen} toggle={toggleModal} />

        <FeedbackModal
            isOpen={showFeedbackModal}
            toggle={toggleFeedbackModal}
        />
      </React.Fragment>
  );
};

ProfileMenu.propTypes = {
  success: PropTypes.any,
  t: PropTypes.any
};

const mapStatetoProps = state => {
  const { error, success } = state.profile;
  return { error, success };
};

export default withRouter(
    connect(mapStatetoProps, {})(withTranslation()(ProfileMenu))
);